import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from '@/components/ui/Logo';
import { CommandPalette } from '@/components/ui/CommandPalette';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { 
  FiLayout, FiTruck, FiBriefcase, FiLogOut, FiMenu, FiBell, FiMoon, 
  FiSun, FiActivity, FiCheckSquare, FiDollarSign, FiUsers, FiPackage, 
  FiGlobe, FiPrinter, FiBarChart2, FiMail, FiClock, FiCheckCircle
} from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import supabase from '@/supabase/supabase';

export function DashboardLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [driverStatus, setDriverStatus] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      return saved === 'dark';
    }
    return false;
  });

  const location = useLocation();
  const { profile, user, signOut } = useAuth();
  const { unreadCount } = useNotifications();

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    let mounted = true;
    if (profile?.role === 'driver' && user) {
      const checkVehicleStatus = async () => {
        const { data: driverProf } = await supabase.from('driver_profiles').select('id').eq('user_id', user.id).maybeSingle();
        if (driverProf) {
          const { data: vehicles } = await supabase.from('driver_vehicles_1778218226805')
            .select('verification_status')
            .eq('driver_id', driverProf.id);
            
          if (mounted) {
            if (!vehicles || vehicles.length === 0) {
              setDriverStatus('no_vehicles');
            } else if (vehicles.some(v => v.verification_status === 'approved')) {
              setDriverStatus('approved');
            } else {
              setDriverStatus('pending');
            }
          }
        }
      };
      checkVehicleStatus();
    }
    return () => { mounted = false; };
  }, [profile, user]);

  const navItems = {
    admin: [
      { icon: FiLayout, label: 'Overview', href: '/dashboard/admin' },
      { icon: FiBriefcase, label: 'Campaigns', href: '/dashboard/admin/campaigns' },
      { icon: FiCheckSquare, label: 'Approvals', href: '/dashboard/admin/approvals' },
      { icon: FiUsers, label: 'Drivers', href: '/dashboard/admin/drivers' },
      { icon: FiBriefcase, label: 'Businesses', href: '/dashboard/admin/businesses' },
      { icon: FiMail, label: 'Emails', href: '/dashboard/admin/emails' },
      { icon: FiPackage, label: 'Packages', href: '/dashboard/admin/packages' },
      { icon: FiDollarSign, label: 'Payouts', href: '/dashboard/admin/payouts' },
      { icon: FiGlobe, label: 'Countries', href: '/dashboard/admin/countries' },
      { icon: FiPrinter, label: 'Print Shops', href: '/dashboard/admin/printing-shops' },
      { icon: FiBarChart2, label: 'Live Stock', href: '/dashboard/admin/stock' },
      { icon: FiActivity, label: 'Activity', href: '/dashboard/admin/activity' },
    ],
    business: [
      { icon: FiLayout, label: 'Overview', href: '/dashboard/business' },
      { icon: FiTruck, label: 'My Campaigns', href: '/dashboard/business/campaigns' },
      { icon: FiActivity, label: 'Analytics', href: '/dashboard/business/analytics' },
    ],
    driver: [
      { icon: FiLayout, label: 'Overview', href: '/dashboard/driver' },
      { icon: FiCheckCircle, label: 'Tasks', href: '/dashboard/driver/tasks' },
      ...(driverStatus === 'approved' ? [
        { icon: FiBriefcase, label: 'Find Work', href: '/dashboard/driver/campaigns' },
        { icon: FiDollarSign, label: 'Earnings', href: '/dashboard/driver/earnings' },
        { icon: FiPrinter, label: 'Print Shops', href: '/dashboard/driver/printing-shops' },
      ] : []),
      { icon: FiTruck, label: 'My Vehicles', href: '/dashboard/driver/vehicles' },
    ],
  };

  const currentNav = navItems[profile?.role] || [];
  const isAdmin = profile?.role === 'admin';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex transition-colors duration-300">
      {isAdmin && <CommandPalette />}
      
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMobileMenuOpen(false)}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-900 border-r dark:border-slate-800 flex flex-col transition-transform duration-300 lg:translate-x-0",
        isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-20 flex items-center px-8 border-b dark:border-slate-800">
          <Logo />
        </div>

        <nav className="flex-1 p-4 space-y-1 mt-4 overflow-y-auto">
          {currentNav.map(item => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all group",
                location.pathname === item.href 
                  ? "bg-primary-500 text-white shadow-lg shadow-primary-500/20" 
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
              )}
            >
              <SafeIcon icon={item.icon} className="w-5 h-5 transition-transform group-hover:scale-110" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t dark:border-slate-800 space-y-1">
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
          >
            <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="w-5 h-5" />
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
          
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all"
          >
            <SafeIcon icon={FiLogOut} className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="h-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b dark:border-slate-800 sticky top-0 z-30 flex items-center justify-between px-6">
          <div className="flex items-center gap-4 flex-1">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <SafeIcon icon={FiMenu} className="w-6 h-6 dark:text-slate-400" />
            </button>
          </div>

          <div className="flex items-center gap-4">
            <Link 
              to="/notifications"
              className="relative p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all group"
            >
              <SafeIcon icon={FiBell} className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2 w-4 h-4 bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white dark:border-slate-800">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
            
            <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-primary-500 to-emerald-400 flex items-center justify-center text-white font-bold shadow-lg shadow-primary-500/20">
              {profile?.full_name ? profile.full_name.charAt(0).toUpperCase() : 'U'}
            </div>
          </div>
        </header>

        {profile?.role === 'driver' && driverStatus === 'pending' && (
          <div className="bg-amber-500 text-white px-6 py-3 text-sm font-bold text-center flex items-center justify-center gap-2 shadow-sm">
            <FiClock className="w-4 h-4 shrink-0" />
            Your vehicle is pending admin verification. We will notify you once approved.
          </div>
        )}

        <main className="flex-1 p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}